import { useCookies } from 'react-cookie';
import GridContainer from '../connectedForm/GridContainer';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { add } from 'date-fns';
import EditableDataGrid from '../EditableDataGrid';
import React, { memo, useContext } from 'react';
import { useId } from '@fluentui/react-hooks';
import { useAssemblyAssignmentColumns, useAssignmentColumns } from './LineDetailAssignmentColumns';
import { AlertType, IPurchaseOrderLine, ISalesOrderLine } from '../../graphql/schema/p4types';
import Row from '../react-data-grid/Row';
import { makeStyles, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, Tooltip } from '@fluentui/react-components';
import { ICommentRowRendererProps } from '../EditableDataGrid/CommentRowRenderer';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import { useP4ToastController } from '../../views/system/NotificationsArea';
import { DeleteRegular } from '@fluentui/react-icons';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';
import { useCheckPermission } from '../../utils/OperationPermissions';
import { PurchaseOrderAction } from '../../actions/purchasing/PurchaseOrderAction';

const INCLUDE_ARCHIVED_ASSIGNMENTS_COOKIE = 'INCLUDE_ARCHIVED_ASSIGNMENTS_COOKIE';

//We somehow need to pass through the first part of the dataid/path to the RowRenderer, using context to achieve this.
const LineDetailContext = React.createContext<{ dataid: string }>({ dataid: '' });

const useStyles = makeStyles({
  menuItem: {
    '&:hover': {
      backgroundColor: p4Tokens.contextMenuHoverBackground1,
      color: p4Tokens.colorNeutralForegroundStaticInverted,
      '& *': {
        color: p4Tokens.colorNeutralForegroundStaticInverted,
      },
    },
  },
});

/**
 * Experimental - New method of attaching a context menu to a row using fluent-ui.
 */
const AssignmentMenuRowRenderer = memo((props: ICommentRowRendererProps) => {
  const { change } = useContext(ConnectedFormContext);
  const { dataid } = useContext(LineDetailContext);
  const { dispatchSimpleToast } = useP4ToastController();
  const styles = useStyles();
  const checkPermission = useCheckPermission();

  const onDelete = () => {
    change(`${dataid}.assignments.${props.idx}.assigned`, 0);
    change(`${dataid}.assignments.${props.idx}.archived`, true);
    dispatchSimpleToast(
      'Assignment Removal',
      'The assignment has been tagged for removal, it will be removed on save.',
      AlertType.Info
    );
  };
  const assignmentDisabled = !checkPermission(PurchaseOrderAction.AssignToSales);
  return (
    <Menu openOnContext={true}>
      <MenuTrigger disableButtonEnhancement>
        <div>
          <Row {...props} />
        </div>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          <Tooltip
            withArrow
            content={
              assignmentDisabled
                ? 'You do not have permission to delete assignments'
                : 'The assignment will be deleted when the order is saved'
            }
            id={'delete-assignment-${props.idx}'}
          >
            <MenuItem
              onClick={onDelete}
              className={styles.menuItem}
              icon={<DeleteRegular />}
              disabled={assignmentDisabled}
            >
              Delete Assignment
            </MenuItem>
          </Tooltip>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
});

export const LineDetailsPanelAssignments = ({
  orderLine,
  type,
  rowIndex,
}: {
  orderLine: ISalesOrderLine | IPurchaseOrderLine;
  type?: 'SALES' | 'PURCHASE';
  rowIndex?: number;
}) => {
  const [cookies, setCookie] = useCookies([INCLUDE_ARCHIVED_ASSIGNMENTS_COOKIE]);
  const archiveFilterTooltipId = useId('archiveFilterTooltipId');

  const assignmentColumns = useAssignmentColumns(type, orderLine?.product);

  return (
    <LineDetailContext.Provider value={{ dataid: `orderLines.${rowIndex}` }}>
      <GridContainer>
        <div style={{ marginTop: '8px' }}>
          <TooltipHost content="Choose whether archived assignments are shown." id={archiveFilterTooltipId}>
            <Toggle
              defaultChecked={cookies[INCLUDE_ARCHIVED_ASSIGNMENTS_COOKIE] === 'true'}
              onText="Including archived assignments"
              offText="Excluding archived assignments"
              onChange={(_, checked) => {
                setCookie(INCLUDE_ARCHIVED_ASSIGNMENTS_COOKIE, checked, {
                  expires: add(new Date(), { years: 1 }),
                });
              }}
              aria-describedby={archiveFilterTooltipId}
            />
          </TooltipHost>
        </div>
        <EditableDataGrid
          columns={assignmentColumns}
          addBlankRow={false}
          enableCellSelect={true}
          enableCellAutoFocus={false}
          listData={(orderLine as ISalesOrderLine | IPurchaseOrderLine).assignments || []}
          rowsCount={
            ((orderLine as ISalesOrderLine | IPurchaseOrderLine).assignments &&
              (orderLine as ISalesOrderLine | IPurchaseOrderLine).assignments?.length) ||
            0
          }
          contentHeight={window.innerHeight - 250}
          hideArchivedLines={cookies[INCLUDE_ARCHIVED_ASSIGNMENTS_COOKIE] !== 'true'}
          rowRenderer={type === 'SALES' ? AssignmentMenuRowRenderer : undefined}
          contextKey={'assignments'}
        />
      </GridContainer>
    </LineDetailContext.Provider>
  );
};

export const LineDetailsPanelAssemblyAssignments = ({
  orderLine,
  type,
  rowIndex,
}: {
  orderLine: IPurchaseOrderLine | ISalesOrderLine;
  type: 'SALES' | 'ASSEMBLY' | 'PURCHASE';
  rowIndex: number;
}) => {
  const [cookies, setCookie] = useCookies([INCLUDE_ARCHIVED_ASSIGNMENTS_COOKIE]);
  const archiveFilterTooltipId = useId('archiveFilterTooltipId');

  const assemblyAssignmentColumns = useAssemblyAssignmentColumns(type);

  return (
    <LineDetailContext.Provider value={{ dataid: `orderLines.${rowIndex}` }}>
      <GridContainer>
        <div style={{ marginTop: '8px' }}>
          <TooltipHost content="Choose whether archived assignments are shown." id={archiveFilterTooltipId}>
            <Toggle
              defaultChecked={cookies[INCLUDE_ARCHIVED_ASSIGNMENTS_COOKIE] === 'true'}
              onText="Including archived assignments"
              offText="Excluding archived assignments"
              onChange={(_, checked) => {
                setCookie(INCLUDE_ARCHIVED_ASSIGNMENTS_COOKIE, checked, {
                  expires: add(new Date(), { years: 1 }),
                });
              }}
              aria-describedby={archiveFilterTooltipId}
            />
          </TooltipHost>
        </div>
        <EditableDataGrid
          columns={assemblyAssignmentColumns}
          addBlankRow={false}
          enableCellSelect={true}
          enableCellAutoFocus={false}
          listData={
            (orderLine as IPurchaseOrderLine).assemblyLineAssignments ||
            (orderLine as ISalesOrderLine).assemblyAssignments ||
            []
          }
          rowsCount={
            (orderLine as IPurchaseOrderLine)?.assemblyLineAssignments?.length ||
            (orderLine as ISalesOrderLine)?.assemblyAssignments?.length ||
            0
          }
          contentHeight={window.innerHeight - 250}
          hideArchivedLines={cookies[INCLUDE_ARCHIVED_ASSIGNMENTS_COOKIE] !== 'true'}
          contextKey={'assemblyAssignments'}
        />
      </GridContainer>
    </LineDetailContext.Provider>
  );
};
